import { useSelector } from 'react-redux';

// material-ui
import Typography from '@mui/material/Typography';

// project imports
import NavGroup from './NavGroup';
import menuItem from 'menu-items';
import userTypes from 'constants/userTypes';
import { useMemo } from 'react';
import { userHasPermission } from '@a1/shared';
import { useGeneralInvoicingAndPaymentsEnabled } from 'hooks/usePayments';
import useFeatureFlags from 'hooks-ts/useFeatureFlags';

// ==============================|| SIDEBAR MENU LIST ||============================== //

const MenuList = () => {
  const user = useSelector((state) => state.user);
  const userData = user?.data;

  const generalSettings = useSelector((state) => state.appConfig?.data?.generalSettings);
  const featureFlags = useFeatureFlags();

  const sidebarVisibilityState = useMemo(() => {
    let sidebarVisibility = {};
    try {
      sidebarVisibility = JSON.parse(generalSettings?.sidebarVisibility);
    } catch {
      sidebarVisibility = {};
    }
    return sidebarVisibility;
  }, [generalSettings]);

  const filterMenuItemsBySidebarVisibility = (menuItems, sidebarVisibilityKey) => {
    if (!sidebarVisibilityKey) {
      return menuItems;
    }

    // If the child ID is present, hide the item; if not present, show the item. This has a default state of showing all items when no settings are configured.
    const filterCriteria = (child) => !sidebarVisibilityState?.[sidebarVisibilityKey]?.[child.id];

    return (
      menuItems
        .map((group) =>
          // 1. Create a copy of the group with the filtered children
          ({
            ...group,
            children: group.children?.filter(filterCriteria) ?? []
          })
        )
        // 2. Remove the group entirely if the filtered children array is empty
        .filter((group) => group.children.length > 0)
    );
  };

  const generalInvoicingAndPaymentsEnabled = useGeneralInvoicingAndPaymentsEnabled();

  const enabledFeatures = { ...featureFlags, invoicingAndPayments: generalInvoicingAndPaymentsEnabled };

  const isMenuItemEnabled = (item) => item.featureFlag === undefined || enabledFeatures[item.featureFlag] === true;

  let baseItems = menuItem.items;
  let sidebarVisibilityKey = null;

  if (userData?.type === userTypes.Manager || userData?.type === userTypes.ExternalUser) {
    baseItems = menuItem.managerItems;
    sidebarVisibilityKey = 'managerItems';
  } else if (userData?.type === userTypes.Admin) {
    baseItems = menuItem.adminItems;
    sidebarVisibilityKey = 'adminItems';
  }

  const sidebarFilteredItems = filterMenuItemsBySidebarVisibility(baseItems, sidebarVisibilityKey);

  const menuItems = sidebarFilteredItems.reduce((acc, groupItem) => {
    if (!isMenuItemEnabled(groupItem)) {
      return acc;
    }

    const filteredChildren = Array.isArray(groupItem.children)
      ? groupItem.children.filter((child) => {
          if (!isMenuItemEnabled(child)) {
            return false;
          }

          const minimumPermissions = Array.isArray(child.minimumPermissions) ? [...child.minimumPermissions] : [];
          return userHasPermission({
            userData,
            validPermissionsAdmin: minimumPermissions,
            validPermissionsManager: minimumPermissions,
            validPermissionsExternalUser: minimumPermissions
          });
        })
      : [];

    // ONLY push the group to our final list if it still has children
    if (filteredChildren.length > 0) {
      acc.push({
        ...groupItem,
        children: filteredChildren
      });
    }

    return acc;
  }, []);

  const navItems = menuItems.map((item) => {
    switch (item.type) {
      case 'group':
        return <NavGroup key={item.id} item={item} />;
      default:
        return (
          <Typography key={item.id} variant="h6" color="error" align="center">
            Menu Items Error
          </Typography>
        );
    }
  });

  return <>{navItems}</>;
};

export default MenuList;
